/*
 * Single shared email normaliser for every outbound Cognito / my-api auth
 * touchpoint (BOARDROOM-2026-08-24-183930 #50 FIX A; audit MY-2026-08-16-092901).
 *
 * Cognito usernames on the consumer pool are case-sensitive while the my-api
 * claim path ALWAYS lowercases (services/my-api/src/store.ts) — an email typed
 * as Case-Variant@Email.com at signup/login therefore minted a case-variant
 * DUPLICATE (or unreachable) Cognito user. Normalise at the BOUNDARY where the
 * value leaves the FE (cognitoAuth / emailProbe / myApi), never deep in
 * components: display values stay exactly as typed.
 *
 * HIDDEN CHARACTERS (boardroom-365 Tier B, 2026-10-31, prod login root cause):
 * a pasted or predictive-text email can carry an internal space or an
 * invisible format character (U+200B zero-width space, U+2060, U+00AD soft
 * hyphen, U+200E/F direction marks, U+FEFF, U+00A0). Cognito rejects such a
 * username with InvalidParameterException, and .trim() removes none of them
 * from inside the string. Every Unicode whitespace, format (Cf) and control
 * (Cc) character is removed ANYWHERE in the address. The same rules live in
 * services/my-api/src/email-normalise.ts.
 */

const HIDDEN_EMAIL_CHARS = /[\p{White_Space}\p{Cf}\p{Cc}]/gu

/** NFC + every whitespace / format / control character removed. Case kept. */
export function stripHiddenEmailChars(email: string): string {
  return email.normalize('NFC').replace(HIDDEN_EMAIL_CHARS, '')
}

/** Case-preserving form: hidden characters removed, domain lowercased, local
 *  part as typed (the legacy mixed-case username fallback). */
export function normalizeEmailKeepLocalCase(email: string): string {
  const s = stripHiddenEmailChars(email)
  const at = s.lastIndexOf('@')
  return at < 0 ? s : s.slice(0, at + 1) + s.slice(at + 1).toLowerCase()
}

/** The canonical outbound form: hidden characters removed, all lowercase. */
export function normalizeEmail(email: string): string {
  return stripHiddenEmailChars(email).toLowerCase()
}

/** For an email INPUT: the cleaned display value (case kept) and whether
 *  anything was removed, so the field can say so. Surrounding ASCII whitespace
 *  alone does not count as a change (browsers strip it from type=email anyway);
 *  a leading/trailing invisible character such as U+FEFF does. */
export function cleanEmailInput(raw: string): { value: string; changed: boolean } {
  const value = stripHiddenEmailChars(raw)
  const asciiTrimmed = raw.normalize('NFC').replace(/^[\t\n\f\r ]+|[\t\n\f\r ]+$/g, '')
  return { value, changed: value !== asciiTrimmed }
}

/** The notice shown under an email field when cleanEmailInput changed it. */
export const HIDDEN_CHAR_NOTICE = 'That email address had a hidden space or character. We’ve removed it.'
